import { AuthApiConfiguration } from '@portal/pkg-api/config'
import { LogoutApi as LogoutApiClz } from '@portal/pkg-api/gen/auth'
import { SELF_HANDLED_FAILURES } from '@auth/resume/requestFailure'
import { finishLogoutContinuation, holdResumeDeparture } from './sessionCoordinator'

export const PrepareLogoutApi = new LogoutApiClz(AuthApiConfiguration)

export interface LogoutContinuation {
  reason?: 'idle'
  expectedSessionInstanceId?: string
}

/**
 * Gets Cognito's logout URL without following it. A 204 means the browser already has another live
 * application session, so this older logout must stop.
 */
async function prepareCognitoLogout({
  reason,
  expectedSessionInstanceId,
}: LogoutContinuation): Promise<string | undefined> {
  // A failure shows this page's own error, never Cognito's login without its logout.
  const response = await PrepareLogoutApi.prepareLogout(
    reason,
    expectedSessionInstanceId,
    SELF_HANDLED_FAILURES,
  )
  if (response.status === 204) {
    return undefined
  }
  const logoutUri = (response.data as { logoutUri?: unknown } | undefined)?.logoutUri
  if (response.status !== 200 || typeof logoutUri !== 'string' || logoutUri.length === 0) {
    throw new Error('The server did not provide the identity-provider logout location.')
  }
  return logoutUri
}

/**
 * Last step before leaving the origin. The server rechecks the current application session, and
 * this page rechecks the cross-tab marker after a logout URL comes back. A 204 has already been
 * refused, so that departure goes to Resume without another marker decision. Either departure holds
 * the marker lock until the document unloads, so a login cannot write its transition between the
 * decision and the navigation. A Resume departure that times out is stopped and fails, because it
 * cannot end a newer identity-provider session and can be retried. A Cognito departure has no
 * timeout: the browser cannot retract a logout request Cognito has already received. A login that
 * completed while either earlier response was delayed therefore wins over the old logout.
 */
export async function continueCognitoLogout(continuation: LogoutContinuation): Promise<void> {
  const location = await prepareCognitoLogout(continuation)
  if (location === undefined) {
    await holdResumeDeparture(() => window.location.replace('/resume'))
    return
  }
  await finishLogoutContinuation(
    continuation.expectedSessionInstanceId,
    () => window.location.replace(location),
    () => window.location.replace('/resume'),
  )
}
